import React from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CustomDialog } from 'components/shared/Dialog/CustomDialog';
import { formatCurrency } from 'utils';
import { DealerResolutionField } from './DealerResolutionField';
import { DetailRow } from './DetailRow';

interface Props {
  isOpen: boolean;
  submitting: boolean;
  onClose: VoidFunction;
  onConfirm: VoidFunction;
  applicantName: string | null;
  contactName: string | null;
  equipmentDescription: string | null;
  requestedAmount: number | null;
  dealerName: string | null;
  brokerName?: string | null;
  dealerNotMatched: boolean;
  selectedDealerDynamicsId: string;
  onDealerChange: (dynamicsId: string) => void;
  formId: string | null;
}

export const CreateNewOpportunitySubmissionModal = ({
  isOpen,
  submitting,
  onClose,
  onConfirm,
  applicantName,
  contactName,
  equipmentDescription,
  requestedAmount,
  dealerName,
  brokerName,
  dealerNotMatched,
  selectedDealerDynamicsId,
  onDealerChange,
  formId,
}: Props) => (
  <CustomDialog
    isOpen={isOpen}
    onClose={onClose}
    label="Create New Opportunity & Submission"
    actions={
      <>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={submitting || (dealerNotMatched && !selectedDealerDynamicsId)}
        >
          Create
        </Button>
      </>
    }
  >
    <Stack spacing={2}>
      <Typography variant="body1">
        A new opportunity and submission will be created using the confirmed application data.
      </Typography>

      {dealerNotMatched && <DealerResolutionField value={selectedDealerDynamicsId} onChange={onDealerChange} />}

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', px: 2, py: 1.5 }}>
        <DetailRow label="Applicant / Business Name:">
          <Typography variant="body2">{applicantName || '—'}</Typography>
        </DetailRow>
        <DetailRow label="Contact:">
          <Typography variant="body2">{contactName || '—'}</Typography>
        </DetailRow>
        <DetailRow label="Equipment:">
          <Typography variant="body2">{equipmentDescription || '—'}</Typography>
        </DetailRow>
        <DetailRow label="Requested Amount:">
          <Typography variant="body2">{requestedAmount != null ? formatCurrency(requestedAmount) : '—'}</Typography>
        </DetailRow>
        <DetailRow label="Dealer Parsed:">
          <Typography variant="body2">{dealerName || '—'}</Typography>
        </DetailRow>
        {brokerName && (
          <DetailRow label="Broker:">
            <Typography variant="body2">{brokerName}</Typography>
          </DetailRow>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <InfoOutlinedIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
        <Box>
          <Typography variant="body2">What will happen next:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
            <Typography component="li" variant="body2" color="text.secondary">
              A new opportunity will be created in TRNSACT.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              A new submission with the confirmed application data is also going to be created in TRNSACT.
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Stack>
  </CustomDialog>
);
